import React, { useState, useMemo, useCallback, useRef } from 'react';
import { X, Plus, Trash2, Film, AlertCircle, Image as ImageIcon, ChevronDown } from 'lucide-react';
import { useTheme } from '@context/ThemeContext';

// 资产选择下拉（支持搜索 + 多选已有的不重复添加）
const AssetPickerDropdown = ({ label, options, selectedIds, onAdd, disabled, theme }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // 点击外部关闭
  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const available = options.filter(opt => !selectedIds.includes(opt.id));

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        disabled={disabled || available.length === 0}
        className="flex items-center gap-1 py-1.5 px-3 text-xs rounded-md transition-all disabled:opacity-40"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          color: 'var(--color-text-secondary)',
          border: '1px solid var(--color-border-primary)'
        }}
      >
        + {label}
        {available.length > 0 && <ChevronDown size={10} className={open ? 'rotate-180' : ''} />}
      </button>
      {open && available.length > 0 && (
        <div
          className="absolute top-full left-0 mt-1 w-56 max-h-48 overflow-y-auto rounded-lg shadow-xl z-50 p-1 custom-scrollbar"
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border-primary)'
          }}
        >
          {available.map(opt => (
            <div
              key={opt.id}
              onClick={() => { onAdd(opt.id); setOpen(false); }}
              className="flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors hover:bg-[var(--color-bg-tertiary)]"
            >
              <div className="w-7 h-7 rounded overflow-hidden shrink-0" style={{ backgroundColor: "var(--color-bg-overlay)" }}>
                {opt.imageUrl && <img src={opt.imageUrl} alt={opt.name} className="w-full h-full object-cover" />}
              </div>
              <span className="text-xs truncate" style={{ color: 'var(--color-text-primary)' }}>{opt.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const MultiShotPanel = ({
  currentShot,
  totalDuration: propTotalDuration,
  characterOptions,
  sceneOptions,
  propOptions,
  onGenerate,
  onClose,
  isGenerating,
  exiting
}) => {
  const { getTheme } = useTheme();
  const theme = getTheme();

  const [refMode, setRefMode] = useState('auto');
  const [customCharacterIds, setCustomCharacterIds] = useState([]);
  const [customSceneIds, setCustomSceneIds] = useState([]);
  const [customPropIds, setCustomPropIds] = useState([]);

  const [focusedShotIndex, setFocusedShotIndex] = useState(null);
  const textareaRefs = useRef({});

  const totalDuration = useMemo(() => {
    if (propTotalDuration) return propTotalDuration;
    const klingDur = currentShot?.video_kling_duration;
    if (klingDur) return Number(klingDur);
    const timeStr = currentShot?.time || '10s';
    return parseInt(timeStr.replace('s', ''), 10) || 10;
  }, [propTotalDuration, currentShot]);

  const [shots, setShots] = useState(() => {
    const half = Math.floor(totalDuration / 2);
    return [
      { index: 1, prompt: '', duration: half },
      { index: 2, prompt: '', duration: totalDuration - half }
    ];
  });

  const allocatedDuration = useMemo(() => shots.reduce((sum, s) => sum + s.duration, 0), [shots]);
  const remainingDuration = totalDuration - allocatedDuration;
  const isDurationValid = remainingDuration === 0;

  // ========== 首帧图片 ==========
  const firstFrameRef = useMemo(() => {
    const img = currentShot?.generated_image;
    if (!img) return null;
    return { id: '__first_frame__', name: '首帧图片', imageUrl: img, isFirstFrame: true };
  }, [currentShot]);

  // ========== 参考图构建（首帧 image_1 排第一） ==========

  const buildRefsWithTags = useCallback((refItems) => {
    let idx = 0;
    const result = [];
    // 首帧永远是 image_1
    if (firstFrameRef) {
      idx++;
      result.push({ ...firstFrameRef, tag: `image_${idx}` });
    }
    for (const item of refItems) {
      idx++;
      result.push({ ...item, tag: `image_${idx}` });
    }
    return result;
  }, [firstFrameRef]);

  const autoRefs = useMemo(() => {
    const rawRefs = [];
    const buildList = (ids, options, variantIndexes) =>
      (ids || []).forEach((id, i) => {
        const item = options.find(opt => opt.id === id);
        if (!item) return;
        const vi = variantIndexes?.[i] ?? -1;
        let imageUrl = item.imageUrl;
        if (vi >= 0 && item.variations?.[vi]) imageUrl = item.variations[vi].imageUrl;
        rawRefs.push({ id, name: item.name, imageUrl, variantIndex: vi, type: 'asset' });
      });
    buildList(currentShot?.refCharacterIds, characterOptions, currentShot?.refCharacterVariantIndexes);
    buildList(currentShot?.refSceneIds, sceneOptions, currentShot?.refSceneVariantIndexes);
    buildList(currentShot?.refPropIds, propOptions, currentShot?.refPropVariantIndexes);
    return buildRefsWithTags(rawRefs);
  }, [currentShot, characterOptions, sceneOptions, propOptions, buildRefsWithTags]);

  const customRefs = useMemo(() => {
    const rawRefs = [];
    const buildList = (ids, options, type) =>
      ids.forEach(id => {
        const item = options.find(opt => opt.id === id);
        if (item) rawRefs.push({ id, name: item.name, imageUrl: item.imageUrl, type });
      });
    buildList(customCharacterIds, characterOptions, 'character');
    buildList(customSceneIds, sceneOptions, 'scene');
    buildList(customPropIds, propOptions, 'prop');
    return buildRefsWithTags(rawRefs);
  }, [customCharacterIds, customSceneIds, customPropIds, characterOptions, sceneOptions, propOptions, buildRefsWithTags]);

  const activeRefs = refMode === 'auto' ? autoRefs : customRefs;
  const refCount = activeRefs.length;
  const maxRefs = 7;
  const isRefLimitReached = refCount >= maxRefs;

  // ========== 镜头操作 ==========

  const handleAddShot = useCallback(() => {
    if (shots.length >= 6) return;
    setShots(prev => [...prev, { index: prev.length + 1, prompt: '', duration: 3 }]);
  }, [shots.length]);

  const handleRemoveShot = useCallback((index) => {
    if (shots.length <= 2) return;
    setShots(prev => prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, index: i + 1 })));
  }, [shots.length]);

  const handleUpdatePrompt = useCallback((index, prompt) => {
    setShots(prev => { const n = [...prev]; n[index] = { ...n[index], prompt }; return n; });
  }, []);

  const handleUpdateDuration = useCallback((index, duration) => {
    const dur = Math.max(1, Math.min(15, parseInt(duration, 10) || 1));
    setShots(prev => { const n = [...prev]; n[index] = { ...n[index], duration: dur }; return n; });
  }, []);

  // ========== 点击参考图插入标签 ==========

  const handleRefClick = useCallback((ref) => {
    if (focusedShotIndex == null) return;
    const textarea = textareaRefs.current[focusedShotIndex];
    if (!textarea) return;

    const tag = `<<<${ref.tag}>>>`;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const cur = shots[focusedShotIndex]?.prompt || '';
    const newPrompt = cur.substring(0, start) + tag + cur.substring(end);
    handleUpdatePrompt(focusedShotIndex, newPrompt);

    requestAnimationFrame(() => {
      textarea.focus();
      const pos = start + tag.length;
      textarea.setSelectionRange(pos, pos);
    });
  }, [focusedShotIndex, shots, handleUpdatePrompt]);

  // ========== 自定义引用操作 ==========

  const handleSwitchToCustom = useCallback(() => {
    setRefMode('custom');
    setCustomCharacterIds(currentShot?.refCharacterIds || []);
    setCustomSceneIds(currentShot?.refSceneIds || []);
    setCustomPropIds(currentShot?.refPropIds || []);
  }, [currentShot]);

  const handleRemoveCustomRef = useCallback((ref) => {
    if (ref.type === 'character') setCustomCharacterIds(prev => prev.filter(id => id !== ref.id));
    else if (ref.type === 'scene') setCustomSceneIds(prev => prev.filter(id => id !== ref.id));
    else if (ref.type === 'prop') setCustomPropIds(prev => prev.filter(id => id !== ref.id));
  }, []);

  // ========== 确认生成 ==========

  const handleConfirm = useCallback(() => {
    if (!isDurationValid) return;

    const multiPrompt = shots.map((shot, i) => ({
      index: i + 1,
      prompt: shot.prompt || currentShot?.video_prompt || currentShot?.visual_content || '',
      duration: String(shot.duration)
    }));

    let refCharacterIds, refSceneIds, refPropIds;
    if (refMode === 'auto') {
      refCharacterIds = currentShot?.refCharacterIds || [];
      refSceneIds = currentShot?.refSceneIds || [];
      refPropIds = currentShot?.refPropIds || [];
    } else {
      refCharacterIds = customCharacterIds;
      refSceneIds = customSceneIds;
      refPropIds = customPropIds;
    }

    onGenerate({
      multiShot: true,
      shotType: 'customize',
      multiPrompt,
      refCharacterIds,
      refSceneIds,
      refPropIds,
      refCharacterVariantIndexes: refMode === 'auto' ? currentShot?.refCharacterVariantIndexes : undefined,
      refSceneVariantIndexes: refMode === 'auto' ? currentShot?.refSceneVariantIndexes : undefined,
      refPropVariantIndexes: refMode === 'auto' ? currentShot?.refPropVariantIndexes : undefined,
    });
  }, [isDurationValid, shots, currentShot, refMode, customCharacterIds, customSceneIds, customPropIds, onGenerate]);

  // ========== 渲染 ==========

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-[var(--overlay-medium)] backdrop-blur-sm ${exiting ? "animate-out fade-out duration-150" : "animate-in fade-in duration-200"}`}>
      <div
        className={`w-full max-w-3xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden ${exiting ? "animate-out fade-out zoom-out-95 duration-150" : "animate-in fade-in zoom-in-95 duration-200"}`}
        style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--color-border-primary)' }}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: theme.colors.purplePrimary }}>
              <Film size={20} style={{ color: "var(--color-accent-contrast)" }} />
            </div>
            <div>
              <h3 className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>多镜头自定义分镜</h3>
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                自定义每个镜头的提示词和时长 · 点击参考图插入引用标签
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg transition-colors hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-muted)' }} disabled={isGenerating}>
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-4">

          {/* 参考图片区域 */}
          <div className="p-4 rounded-lg space-y-3" style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-primary)' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ImageIcon size={16} style={{ color: theme.colors.bluePrimary }} />
                <span className="text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>参考图片</span>
                <span className="text-2xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${theme.colors.bluePrimary}20`, color: theme.colors.bluePrimary }}>
                  点击插入到提示词
                </span>
              </div>
              {refMode === 'auto' ? (
                <button onClick={handleSwitchToCustom} className="text-xs px-3 py-1 rounded-md" style={{ backgroundColor: 'var(--color-bg-primary)', color: theme.colors.bluePrimary, border: `1px solid ${theme.colors.bluePrimary}` }} disabled={isGenerating}>
                  自定义选择
                </button>
              ) : (
                <button onClick={() => setRefMode('auto')} className="text-xs px-3 py-1 rounded-md" style={{ backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border-primary)' }} disabled={isGenerating}>
                  使用分镜引用
                </button>
              )}
            </div>

            {/* 引用列表 */}
            {activeRefs.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {activeRefs.map((ref, i) => {
                  const canInsert = focusedShotIndex != null;
                  const isFirst = ref.isFirstFrame;
                  return (
                    <div
                      key={`${ref.tag}-${i}`}
                      className={`relative group ${canInsert ? 'cursor-pointer' : ''}`}
                      onClick={() => canInsert && handleRefClick(ref)}
                      title={canInsert ? `点击插入 <<<${ref.tag}>>>` : '先点击某个镜头的提示词输入框'}
                    >
                      <div
                        className={`flex items-center gap-2 px-2 py-1.5 rounded-md transition-all ${canInsert ? 'hover:ring-2' : ''}`}
                        style={{
                          backgroundColor: 'var(--color-bg-primary)',
                          border: `1px solid ${isFirst ? theme.colors.purplePrimary + '60' : 'var(--color-border-primary)'}`,
                          '--tw-ring-color': theme.colors.bluePrimary,
                        }}
                      >
                        <div className="w-8 h-8 rounded overflow-hidden shrink-0" style={{ backgroundColor: "var(--color-bg-overlay)" }}>
                          {ref.imageUrl && <img src={ref.imageUrl} alt={ref.name} className="w-full h-full object-cover" />}
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="text-2xs font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                            {ref.name}
                          </span>
                          <span className="text-2xs font-mono" style={{ color: isFirst ? theme.colors.purplePrimary : theme.colors.bluePrimary }}>
                            {ref.tag}{isFirst ? ' (首帧)' : ''}
                          </span>
                        </div>
                        {/* 自定义模式下非首帧可删除 */}
                        {refMode === 'custom' && !isFirst && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRemoveCustomRef(ref); }}
                            className="ml-1 p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{ color: 'var(--color-text-muted)' }}
                            disabled={isGenerating}
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-xs text-center py-4" style={{ color: 'var(--color-text-muted)' }}>
                {refMode === 'auto' ? '当前分镜未绑定资产引用' : '未选择任何参考图'}
              </div>
            )}

            {/* 自定义模式：资产选择器 */}
            {refMode === 'custom' && (
              <div className="flex gap-2 flex-wrap">
                <AssetPickerDropdown
                  label="角色"
                  options={characterOptions}
                  selectedIds={customCharacterIds}
                  onAdd={(id) => setCustomCharacterIds(prev => [...prev, id])}
                  disabled={isRefLimitReached || isGenerating}
                  theme={theme}
                />
                <AssetPickerDropdown
                  label="场景"
                  options={sceneOptions}
                  selectedIds={customSceneIds}
                  onAdd={(id) => setCustomSceneIds(prev => [...prev, id])}
                  disabled={isRefLimitReached || isGenerating}
                  theme={theme}
                />
                <AssetPickerDropdown
                  label="道具"
                  options={propOptions}
                  selectedIds={customPropIds}
                  onAdd={(id) => setCustomPropIds(prev => [...prev, id])}
                  disabled={isRefLimitReached || isGenerating}
                  theme={theme}
                />
              </div>
            )}

            {/* 状态提示 */}
            <div className="flex items-center justify-between text-2xs" style={{ color: 'var(--color-text-muted)' }}>
              <span>
                {focusedShotIndex != null
                  ? `点击参考图 → 插入 <<<image_N>>> 到镜头 ${focusedShotIndex + 1}`
                  : '先点击某个镜头的提示词输入框，再点击参考图插入引用'}
              </span>
              <span style={{ color: refCount >= maxRefs ? theme.colors.error : 'inherit', fontWeight: refCount >= maxRefs ? 'bold' : 'normal' }}>
                {refCount}/{maxRefs}
              </span>
            </div>
          </div>

          {/* 时长统计 */}
          <div
            className="flex items-center justify-between px-4 py-3 rounded-lg"
            style={{
              backgroundColor: isDurationValid ? 'var(--color-bg-overlay)' : `${theme.colors.error}10`,
              border: `1px solid ${isDurationValid ? 'var(--color-border-primary)' : theme.colors.error}`
            }}
          >
            <div className="flex items-center gap-4 text-sm">
              <span style={{ color: 'var(--color-text-muted)' }}>
                总时长: <span className="font-bold" style={{ color: 'var(--color-text-primary)' }}>{totalDuration}s</span>
              </span>
              <span style={{ color: 'var(--color-text-muted)' }}>
                已分配: <span className="font-bold" style={{ color: 'var(--color-text-primary)' }}>{allocatedDuration}s</span>
              </span>
              <span style={{ color: 'var(--color-text-muted)' }}>
                剩余: <span className="font-bold" style={{ color: remainingDuration === 0 ? theme.colors.success : theme.colors.error }}>
                  {remainingDuration}s
                </span>
              </span>
            </div>
            {!isDurationValid && (
              <div className="flex items-center gap-1 text-xs" style={{ color: theme.colors.error }}>
                <AlertCircle size={14} />
                <span>{remainingDuration > 0 ? `还需分配 ${remainingDuration}s` : `超出 ${-remainingDuration}s`}</span>
              </div>
            )}
          </div>

          {/* 镜头列表 */}
          <div className="space-y-3">
            {shots.map((shot, index) => (
              <div
                key={shot.index}
                className="p-4 rounded-lg space-y-2 transition-all"
                style={{
                  backgroundColor: 'var(--color-bg-overlay)',
                  border: `1px solid ${focusedShotIndex === index ? theme.colors.bluePrimary : 'var(--color-border-primary)'}`,
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold px-2 py-1 rounded" style={{ backgroundColor: theme.colors.purplePrimary, color: 'white' }}>
                      镜头 {shot.index}
                    </span>
                    {focusedShotIndex === index && (
                      <span className="text-2xs px-1.5 py-0.5 rounded-full animate-pulse"
                        style={{ backgroundColor: `${theme.colors.bluePrimary}20`, color: theme.colors.bluePrimary }}>
                        编辑中
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <label className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>时长:</label>
                      <input
                        type="number" min={1} max={15}
                        value={shot.duration}
                        onChange={(e) => handleUpdateDuration(index, e.target.value)}
                        className="w-14 text-xs px-2 py-1 rounded text-center outline-none"
                        style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                        disabled={isGenerating}
                      />
                      <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>s</span>
                    </div>
                    {shots.length > 2 && (
                      <button onClick={() => handleRemoveShot(index)} className="p-1 rounded transition-colors hover:bg-[var(--color-bg-tertiary)]" style={{ color: theme.colors.error }} disabled={isGenerating}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>

                <textarea
                  ref={(el) => { textareaRefs.current[index] = el; }}
                  value={shot.prompt}
                  onChange={(e) => handleUpdatePrompt(index, e.target.value)}
                  onFocus={() => setFocusedShotIndex(index)}
                  placeholder={`镜头 ${shot.index} 的描述... 点击上方参考图插入 <<<image_N>>>`}
                  maxLength={512}
                  rows={3}
                  className="w-full text-xs px-3 py-2 rounded-lg resize-none outline-none transition-all"
                  style={{
                    backgroundColor: 'var(--color-bg-primary)',
                    border: `1px solid ${focusedShotIndex === index ? theme.colors.bluePrimary : 'var(--color-border-primary)'}`,
                    color: 'var(--color-text-primary)'
                  }}
                  disabled={isGenerating}
                />
                <div className="text-2xs text-right" style={{ color: 'var(--color-text-muted)' }}>
                  {shot.prompt.length}/512
                </div>
              </div>
            ))}
          </div>

          {/* 添加镜头 */}
          {shots.length < 6 && (
            <button
              onClick={handleAddShot}
              className="w-full py-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2"
              style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px dashed var(--color-border-primary)', color: 'var(--color-text-secondary)' }}
              disabled={isGenerating}
            >
              <Plus size={16} /> 添加镜头 (最多6个)
            </button>
          )}

          {/* 提示 */}
          <div className="p-3 rounded-lg text-xs space-y-1" style={{ backgroundColor: `${theme.colors.bluePrimary}10`, border: `1px solid ${theme.colors.bluePrimary}40` }}>
            <div style={{ color: theme.colors.bluePrimary }}>使用说明:</div>
            <ul className="list-disc list-inside space-y-0.5" style={{ color: 'var(--color-text-secondary)' }}>
              <li>点击提示词输入框，再点击上方参考图，自动插入 <code className="font-mono text-2xs px-1 rounded" style={{ backgroundColor: 'var(--color-bg-primary)' }}>{`<<<image_N>>>`}</code> 引用标签</li>
              <li>所有镜头时长总和需等于 {totalDuration}s（每个镜头 1-15s）</li>
              <li>每个镜头提示词最多 512 字符</li>
              <li>首帧图片 (image_1) 自动应用到第一个镜头</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex items-center justify-end gap-3" style={{ backgroundColor: 'var(--color-bg-primary)', borderColor: 'var(--color-border-primary)' }}>
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium" style={{ backgroundColor: 'var(--color-bg-overlay)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border-primary)' }} disabled={isGenerating}>
            取消
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isDurationValid || isGenerating}
            className="px-6 py-2 rounded-lg text-sm font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: theme.colors.purplePrimary, color: 'white' }}
          >
            {isGenerating ? '生成中...' : '确认生成'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiShotPanel;
